/**
 * Estratto Agenti (admin) — gestione provvigioni agenti e pagamenti (anche parziali).
 * - Riepilogo per agente (maturate / pagate / residue)
 * - Dettaglio documenti con selezione → registra pagamento (auto cronologico o manuale per doc)
 * - Storico pagamenti con eliminazione
 */
import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Wallet, ArrowLeft, Banknote, Trash2, RefreshCw } from "lucide-react";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Input } from "./ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import CommissionRowsTable, { CommissionGroups } from "./CommissionRowsTable";
import { API } from "../utils/format";
import { formatMoney } from "../utils/currency";

const fmtDate = (iso) => (iso ? new Date(iso + (iso.length === 10 ? "T00:00:00" : "")).toLocaleDateString("it-IT") : "—");

const KPI = ({ label, value, accent = "text-stone-900", testId }) => (
  <Card className="bg-white border-stone-200 p-3" data-testid={testId}>
    <p className="text-[10px] text-stone-500 uppercase tracking-wide">{label}</p>
    <p className={`text-lg font-bold ${accent}`}>{value}</p>
  </Card>
);

function PaymentDialog({ open, onClose, agent, selectedRows, company, onSaved }) {
  const selResidual = selectedRows.reduce((s, r) => s + r.commission_residual, 0);
  const [amount, setAmount] = useState("");
  const [payDate, setPayDate] = useState(new Date().toISOString().slice(0, 10));
  const [note, setNote] = useState("");
  const [mode, setMode] = useState("auto");
  const [manualAlloc, setManualAlloc] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setAmount(selResidual > 0 ? selResidual.toFixed(2) : "");
      setNote(""); setMode("auto");
      setManualAlloc(Object.fromEntries(selectedRows.map((r) => [r.row_id, r.commission_residual.toFixed(2)])));
      setPayDate(new Date().toISOString().slice(0, 10));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const manualTotal = Object.values(manualAlloc).reduce((s, v) => s + (parseFloat(String(v).replace(",", ".")) || 0), 0);

  const submit = async () => {
    setSaving(true);
    try {
      const body = { payment_date: payDate, note };
      if (mode === "manual") {
        body.allocations = selectedRows
          .map((r) => ({ row_id: r.row_id, amount: parseFloat(String(manualAlloc[r.row_id] || "0").replace(",", ".")) || 0 }))
          .filter((a) => a.amount > 0);
        body.amount = manualTotal;
      } else {
        body.amount = parseFloat(String(amount).replace(",", "."));
        if (selectedRows.length > 0) body.doc_row_ids = selectedRows.map((r) => r.row_id);
      }
      const r = await axios.post(`${API}/admin/agent-commissions/${agent.id}/payments`, body);
      toast.success(`Pagamento registrato: ${formatMoney(r.data.amount, company)} su ${r.data.allocations_count} documenti`);
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nel salvataggio del pagamento");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Banknote className="w-5 h-5 text-[#8A1C2A]" /> Paga provvigioni — {agent?.name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="bg-stone-50 border border-stone-200 rounded-md p-3 text-xs">
            <p><strong>{selectedRows.length}</strong> documenti selezionati — residuo maturato: <strong className="text-[#8A1C2A]">{formatMoney(selResidual, company)}</strong></p>
            {selectedRows.length === 0 && <p className="text-stone-500 mt-1">Nessun documento selezionato: l'acconto verrà scalato dalle provvigioni più vecchie di tutto l'estratto.</p>}
          </div>
          <div className="flex gap-2">
            <button
              className={`flex-1 text-xs border rounded-md py-2 px-2 ${mode === "auto" ? "border-[#8A1C2A] bg-[#8A1C2A]/5 font-semibold text-[#8A1C2A]" : "border-stone-200 text-stone-600"}`}
              onClick={() => setMode("auto")} data-testid="ac-pay-mode-auto"
            >
              Automatico<span className="block font-normal text-[10px] text-stone-500">acconto scalato dalle più vecchie</span>
            </button>
            <button
              className={`flex-1 text-xs border rounded-md py-2 px-2 ${mode === "manual" ? "border-[#8A1C2A] bg-[#8A1C2A]/5 font-semibold text-[#8A1C2A]" : "border-stone-200 text-stone-600"}`}
              onClick={() => setMode("manual")} disabled={selectedRows.length === 0} data-testid="ac-pay-mode-manual"
            >
              Manuale<span className="block font-normal text-[10px] text-stone-500">importo per documento</span>
            </button>
          </div>
          {mode === "auto" ? (
            <div>
              <label className="text-xs text-stone-600">Importo pagamento (€) — anche parziale/acconto</label>
              <Input value={amount} onChange={(e) => setAmount(e.target.value)} className="h-9 mt-1" data-testid="ac-pay-amount" />
            </div>
          ) : (
            <div className="max-h-56 overflow-y-auto border border-stone-200 rounded-md divide-y divide-stone-100">
              {selectedRows.map((r) => (
                <div key={r.row_id} className="flex items-center gap-2 p-2 text-xs">
                  <div className="flex-1 min-w-0">
                    <p className="font-mono">{r.doc_number || r.order_number}</p>
                    <p className="text-stone-500 truncate">{r.customer_name} · residuo {formatMoney(r.commission_residual, company)}</p>
                  </div>
                  <Input
                    value={manualAlloc[r.row_id] ?? ""}
                    onChange={(e) => setManualAlloc((m) => ({ ...m, [r.row_id]: e.target.value }))}
                    className="h-8 w-24 text-right"
                    data-testid={`ac-pay-alloc-${r.row_id}`}
                  />
                </div>
              ))}
              <div className="p-2 text-xs font-semibold text-right bg-stone-50">Totale: {formatMoney(manualTotal, company)}</div>
            </div>
          )}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs text-stone-600">Data pagamento</label>
              <Input type="date" value={payDate} onChange={(e) => setPayDate(e.target.value)} className="h-9 mt-1" data-testid="ac-pay-date" />
            </div>
            <div>
              <label className="text-xs text-stone-600">Nota (es. bonifico n.)</label>
              <Input value={note} onChange={(e) => setNote(e.target.value)} className="h-9 mt-1" data-testid="ac-pay-note" />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-stone-300">Annulla</Button>
          <Button onClick={submit} disabled={saving} className="bg-[#8A1C2A] hover:bg-[#6B1420] text-white" data-testid="ac-pay-submit">
            {saving ? "Salvataggio..." : "Registra pagamento"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function AgentCommissionsAdmin({ company }) {
  const [summary, setSummary] = useState([]);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [filter, setFilter] = useState("residual");
  const [search, setSearch] = useState("");
  const [period, setPeriod] = useState("all");
  const [view, setView] = useState("customer");
  const [payOpen, setPayOpen] = useState(false);
  const [recomputing, setRecomputing] = useState(false);

  const loadSummary = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/agent-commissions/summary`);
      setSummary(r.data || []);
    } catch { toast.error("Errore caricamento estratto agenti"); }
    finally { setLoading(false); }
  }, []);

  const loadDetail = useCallback(async (agentId) => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/agent-commissions/${agentId}`);
      setDetail(r.data);
      setSelected(new Set());
    } catch { toast.error("Errore caricamento dettaglio agente"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadSummary(); }, [loadSummary]);

  const deletePayment = async (pid) => {
    if (!window.confirm("Eliminare questo pagamento? Le provvigioni torneranno da pagare.")) return;
    try {
      await axios.delete(`${API}/admin/agent-commissions/payments/${pid}`);
      toast.success("Pagamento eliminato");
      loadDetail(detail.agent.id);
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  const visibleRows = (detail?.rows || []).filter((r) => {
    if (filter === "residual" && r.commission_residual <= 0.005) return false;
    if (filter === "paid" && r.commission_residual > 0.005) return false;
    if (period !== "all" && !(r.doc_date || "").startsWith(period)) return false;
    if (search) {
      const q = search.toLowerCase();
      if (!(r.customer_name || "").toLowerCase().includes(q) && !(r.doc_number || "").toLowerCase().includes(q) && !(r.order_number || "").toLowerCase().includes(q)) return false;
    }
    return true;
  });
  const months = [...new Set((detail?.rows || []).map((r) => (r.doc_date || "").slice(0, 7)).filter(Boolean))].sort().reverse();
  const selectedRows = (detail?.rows || []).filter((r) => selected.has(r.row_id));
  const selResidual = selectedRows.reduce((s, r) => s + r.commission_residual, 0);

  const toggle = (rowId) => setSelected((s) => { const n = new Set(s); n.has(rowId) ? n.delete(rowId) : n.add(rowId); return n; });
  const toggleAll = () => {
    const selectable = visibleRows.map((r) => r.row_id);
    setSelected((s) => (selectable.every((id) => s.has(id)) ? new Set() : new Set(selectable)));
  };
  const toggleGroup = (rowIds) => setSelected((s) => {
    const n = new Set(s);
    const all = rowIds.every((id) => n.has(id));
    rowIds.forEach((id) => (all ? n.delete(id) : n.add(id)));
    return n;
  });

  const recompute = async () => {
    if (!window.confirm(`Ricalcolare le provvigioni dei ${selected.size} documenti selezionati con i rate ATTUALI (Cliente → Articolo → Agente → Listino)?\n\nGli importi storici degli ordini collegati verranno sovrascritti.`)) return;
    setRecomputing(true);
    try {
      const r = await axios.post(`${API}/admin/agent-commissions/recompute`, { row_ids: [...selected] });
      const d = r.data;
      const changes = (d.results || []).filter((x) => Math.abs(x.old - x.new) > 0.005);
      toast.success(
        `Ricalcolati ${d.orders_updated} ordini (${changes.length} variati)` +
        (d.skipped_without_orders ? ` — ${d.skipped_without_orders} documenti senza ordini collegati esclusi (usano già il rate attuale)` : "")
      );
      loadDetail(detail.agent.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nel ricalcolo provvigioni");
    } finally { setRecomputing(false); }
  };

  if (loading && !detail && summary.length === 0) return <div className="p-8 text-center"><div className="spinner w-6 h-6 mx-auto" /></div>;

  // ============ VISTA DETTAGLIO AGENTE ============
  if (detail) {
    const t = detail.totals;
    return (
      <div className="space-y-4" data-testid="ac-detail">
        <div className="flex items-center gap-3 flex-wrap">
          <Button variant="outline" size="sm" onClick={() => { setDetail(null); loadSummary(); }} className="border-stone-300" data-testid="ac-back-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> Agenti
          </Button>
          <h2 className="text-xl font-semibold text-stone-900 flex items-center gap-2">
            <Wallet className="w-5 h-5 text-[#8A1C2A]" /> {detail.agent.name}
          </h2>
          <Button variant="ghost" size="sm" onClick={() => loadDetail(detail.agent.id)} className="text-stone-500"><RefreshCw className="w-4 h-4" /></Button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <KPI label="Fatturato complessivo" value={formatMoney(t.invoiced, company)} testId="ac-kpi-invoiced" />
          <KPI label="Provvigioni totali" value={formatMoney(t.due, company)} testId="ac-kpi-due" />
          <KPI label="Maturate (su incassato)" value={formatMoney(t.matured, company)} accent="text-blue-700" testId="ac-kpi-matured" />
          <KPI label="Pagate all'agente" value={formatMoney(t.paid, company)} accent="text-green-700" testId="ac-kpi-paid" />
          <KPI label="Residue da pagare" value={formatMoney(t.residual, company)} accent="text-[#8A1C2A]" testId="ac-kpi-residual" />
        </div>

        <Card className="p-0 overflow-hidden bg-white border-stone-200">
          <div className="p-3 border-b border-stone-100 flex items-center gap-2 flex-wrap">
            <Input placeholder="Cerca cliente o documento..." value={search} onChange={(e) => setSearch(e.target.value)} className="max-w-xs h-8" data-testid="ac-search" />
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger className="w-36 h-8 text-xs" data-testid="ac-period"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti i periodi</SelectItem>
                {months.map((m) => <SelectItem key={m} value={m}>{m.split("-")[1]}/{m.split("-")[0]}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger className="w-44 h-8 text-xs" data-testid="ac-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="residual">Con residuo da pagare</SelectItem>
                <SelectItem value="paid">Saldate / senza residuo</SelectItem>
                <SelectItem value="all">Tutti i documenti</SelectItem>
              </SelectContent>
            </Select>
            <div className="flex rounded-md border border-stone-200 overflow-hidden text-xs">
              <button className={`px-3 py-1.5 ${view === "customer" ? "bg-[#8A1C2A] text-white font-semibold" : "bg-white text-stone-600"}`} onClick={() => setView("customer")} data-testid="ac-view-customer">Per cliente</button>
              <button className={`px-3 py-1.5 ${view === "list" ? "bg-[#8A1C2A] text-white font-semibold" : "bg-white text-stone-600"}`} onClick={() => setView("list")} data-testid="ac-view-list">Elenco</button>
            </div>
            <span className="ml-auto text-xs text-stone-500">{visibleRows.length} documenti</span>
          </div>
          {view === "customer"
            ? <CommissionGroups rows={visibleRows} company={company} selectable selected={selected} onToggle={toggle} onToggleGroup={toggleGroup} />
            : <CommissionRowsTable rows={visibleRows} company={company} selectable selected={selected} onToggle={toggle} onToggleAll={toggleAll} />}
          <div className="h-16" />
        </Card>

        {/* Barra selezione */}
        <div className="sticky bottom-2 z-10">
          <Card className={`p-3 flex items-center gap-3 flex-wrap border shadow-lg ${selected.size > 0 ? "bg-[#8A1C2A] text-white border-[#8A1C2A]" : "bg-white border-stone-200"}`} data-testid="ac-selected-bar">
            <p className="text-sm">
              {selected.size > 0
                ? <><strong>{selected.size}</strong> documenti selezionati — residuo <strong>{formatMoney(selResidual, company)}</strong></>
                : <span className="text-stone-500">Seleziona i documenti da pagare, oppure registra un acconto su tutto l'estratto</span>}
            </p>
            <Button
              variant="outline"
              onClick={recompute}
              disabled={selected.size === 0 || recomputing}
              className={`ml-auto ${selected.size > 0 ? "border-white/60 text-white bg-transparent hover:bg-white/10" : "border-stone-300 text-stone-600"}`}
              title="Ricalcola le provvigioni degli ordini selezionati con i rate attuali di agente/cliente/listino"
              data-testid="ac-recompute-btn"
            >
              <RefreshCw className={`w-4 h-4 mr-1.5 ${recomputing ? "animate-spin" : ""}`} /> {recomputing ? "Ricalcolo..." : "Ricalcola rate attuale"}
            </Button>
            <Button
              onClick={() => setPayOpen(true)}
              disabled={t.residual <= 0.005}
              className={`${selected.size > 0 ? "bg-white text-[#8A1C2A] hover:bg-stone-100" : "bg-[#8A1C2A] text-white hover:bg-[#6B1420]"}`}
              data-testid="ac-pay-btn"
            >
              <Banknote className="w-4 h-4 mr-1.5" /> Registra pagamento
            </Button>
          </Card>
        </div>

        {/* Storico pagamenti */}
        <Card className="p-0 overflow-hidden bg-white border-stone-200">
          <div className="p-3 border-b border-stone-100 font-semibold text-sm text-stone-800">Storico pagamenti provvigioni</div>
          {(detail.payments || []).length === 0 ? (
            <p className="p-4 text-sm text-stone-500 italic">Nessun pagamento registrato</p>
          ) : (
            <div className="divide-y divide-stone-100">
              {detail.payments.map((p) => (
                <div key={p.id} className="p-3 flex items-center gap-3 text-sm" data-testid={`ac-payment-${p.id}`}>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-stone-900">{formatMoney(p.amount, company)} <span className="font-normal text-stone-500">— {fmtDate(p.payment_date)}</span></p>
                    <p className="text-xs text-stone-500 truncate">
                      {p.allocations?.length || 0} documenti{p.note ? ` · ${p.note}` : ""}
                    </p>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => deletePayment(p.id)} className="text-red-600 hover:bg-red-50" data-testid={`ac-payment-delete-${p.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </Card>

        <PaymentDialog open={payOpen} onClose={() => setPayOpen(false)} agent={detail.agent} selectedRows={selectedRows} company={company} onSaved={() => loadDetail(detail.agent.id)} />
      </div>
    );
  }

  // ============ VISTA RIEPILOGO AGENTI ============
  return (
    <div className="space-y-4" data-testid="agent-commissions-page">
      <div>
        <h2 className="text-2xl font-semibold text-stone-900 flex items-center gap-2"><Wallet className="w-6 h-6 text-[#8A1C2A]" /> Estratto Agenti</h2>
        <p className="text-stone-500 text-sm">Provvigioni maturate sull'incassato dei clienti (documenti app + storico TeamSystem) e pagamenti agli agenti</p>
      </div>
      <Card className="p-0 overflow-hidden bg-white border-stone-200">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[700px]">
            <thead className="bg-stone-100 text-left text-xs uppercase text-stone-600">
              <tr>
                <th className="p-3">Agente</th>
                <th className="p-3 text-center">Documenti</th>
                <th className="p-3 text-right">Fatturato</th>
                <th className="p-3 text-right">Provv. totali</th>
                <th className="p-3 text-right">Maturate</th>
                <th className="p-3 text-right">Pagate</th>
                <th className="p-3 text-right">Residue</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {summary.length === 0 && <tr><td colSpan={8} className="p-8 text-center text-stone-500 italic">Nessun agente</td></tr>}
              {summary.map((a) => (
                <tr key={a.agent_id} className="border-t border-stone-100 hover:bg-stone-50 cursor-pointer" onClick={() => loadDetail(a.agent_id)} data-testid={`ac-agent-row-${a.agent_id}`}>
                  <td className="p-3">
                    <span className="font-semibold text-stone-900">{a.agent_name}</span>
                    {a.commission_rate != null && <Badge className="ml-2 bg-stone-100 text-stone-600 border border-stone-200 text-[10px] py-0">{(a.commission_rate * 100).toFixed(1)}%</Badge>}
                  </td>
                  <td className="p-3 text-center text-stone-600">{a.docs_count}</td>
                  <td className="p-3 text-right font-semibold" data-testid={`ac-invoiced-${a.agent_id}`}>{formatMoney(a.invoiced, company)}</td>
                  <td className="p-3 text-right">{formatMoney(a.due, company)}</td>
                  <td className="p-3 text-right text-blue-700">{formatMoney(a.matured, company)}</td>
                  <td className="p-3 text-right text-green-700">{formatMoney(a.paid, company)}</td>
                  <td className={`p-3 text-right font-bold ${a.residual > 0.005 ? "text-[#8A1C2A]" : "text-stone-400"}`}>{formatMoney(a.residual, company)}</td>
                  <td className="p-3 text-right">
                    <Button size="sm" variant="outline" className="border-stone-300 h-7 text-xs" data-testid={`ac-open-detail-${a.agent_id}`}>Estratto</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
